import { useEffect, useMemo, useState } from 'react';
import useLocales from 'src/hooks/useLocales';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import {
  Box,
  Card,
  Table,
  Button,
  TableBody,
  Container,
  TableContainer,
  Grid,
  Stack,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  TableCell,
} from '@mui/material';

import { observer } from 'mobx-react-lite';

import PermissionBasedGuard from 'src/guards/PermissionBasedGuard';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';

import useSettings from 'src/hooks/useSettings';
import { PATH_DASHBOARD } from 'src/routes/paths';
import Page from 'src/components/Page';
import { useStore } from 'src/stores/store';
import {
  IElectricityPaymentDetailsParam,
  IElectricityPaymentParams,
} from 'src/@types/Choqorak/MainTables/ElectricityPayment/ElectricityPayment';

import { useForm } from 'react-hook-form';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';
import Loader from 'src/components/loader/Loader';
import MyDialog from 'src/components/MyDialog';

import Label from 'src/components/Label';

import ELectricConsumption from '../../../Settings/ElectricMeter/ElectricMeterList/ELectricConsumption';
import ElectricityConsumptionToolbar from './ElectricityConsumptionToolbar';
import { SortByFieldSmart } from 'src/Common/SortByFieldSmart';

// ----------------------------------------------------------------------
export default observer(function ElectricityConsumptionList() {
  const { ElectricMeterStore } = useStore();
  const { translate } = useLocales();

  const {
    getElectricMeterFromRegistry,
    setOpenCloseConsumptionDialog,
    openConsuptionDialog,
    loadElectricConsumptionPayment,
    ElectricMeterConsumptionList,
  } = ElectricMeterStore;

  const { themeStretch } = useSettings();
  const [isloading, setIsloading] = useState(false);
  const [year, setYear] = useState<string>(''); // State for the year
  const [month, setMonth] = useState<string>(''); // State for the month
  const [searchBy, setSearchBy] = useState<string>('');

  const [ElectricMeterId] = useState<number>(0);

  //#region
  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);

  const defaultValues = useMemo<IElectricityPaymentParams>(
    () => ({
      years: new Date(),
    }),
    []
  );
  const methods = useForm<IElectricityPaymentParams>({
    defaultValues,
  });
  const { watch, control } = methods;
  const val = watch();
  const yearsDate = watch('years'); // Watching the 'years' field

  useEffect(() => {
    if (yearsDate) {
      const changeDateToDateFormat = new Date(yearsDate);

      // Extract Persian year and month
      const persianYear = changeDateToDateFormat.toLocaleString('fa-IR-u-ca-persian', {
        year: 'numeric',
      });
      const persianMonth = changeDateToDateFormat.toLocaleString('fa-IR-u-ca-persian', {
        month: 'numeric',
      });

      // Convert Persian numerals to Western numerals
      const westernYear = persianYear.replace(/[۰-۹]/g, (digit) =>
        '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
      );
      const westernMonth = persianMonth.replace(/[۰-۹]/g, (digit) =>
        '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
      );

      // Update state with Western year and month
      setYear(westernYear);
      setMonth(westernMonth);
    }
  }, [yearsDate]);

  const Search = async (e: any) => {
    e.preventDefault();
    setIsloading(true);
    loadElectricConsumptionPayment({
      year: Number(year),
      month: Number(month),
      searchBy: searchBy,
    })
      .then((res) => {
        setIsloading(false);
      })
      .catch((err) => {
        setIsloading(false);
      });
  };

  useEffect(() => { }, [openMenu]);
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchBy(event.target.value); // Update state when input changes
  };

  const handleConsumption = (id: number) => {
    getElectricMeterFromRegistry(id);
    setOpenCloseConsumptionDialog();
  };

  const handleCloseConsumptionConfirm = () => {
    setOpenCloseConsumptionDialog();
  };
  useEffect(() => {
    console.log('ElectricMeterConsumptionList updated', ElectricMeterConsumptionList);
  }, [ElectricMeterConsumptionList]);

  const paymentCounts = ElectricMeterConsumptionList?.reduce(
    (acc, row) => {
      if (row.hasPamentRecord) {
        acc.trueCount++;
      } else {
        acc.falseCount++;
      }
      acc.uniqueShops.add(row.shopCode); // Using a Set to count unique shop codes
      return acc;
    },
    { trueCount: 0, falseCount: 0, uniqueShops: new Set() }
  );

  const totalShops = paymentCounts?.uniqueShops.size || 0;
  const totalPaid = paymentCounts?.trueCount || 0;
  const totalUnpaid = paymentCounts?.falseCount || 0;
  return (
    <Page title={translate('ElectricMeter.Consumption')}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={translate('ElectricMeter.ConsumptionList')}
          links={[
            { name: `${translate('Department.Dashboard')}`, href: PATH_DASHBOARD.root },

            { name: `${translate('ElectricMeter.ConsumptionList')}` },
          ]}
          action={
            <>
              <PermissionBasedGuard permissions={['Contract-Create']}>
                <Button
                  variant="contained"
                  startIcon={<Iconify icon="eva:plus-fill" />}
                  component={RouterLink}
                  to={PATH_DASHBOARD.ElectricityPayment.list}
                  color="error"
                >
                  {translate('CRUD.Back')}
                </Button>
              </PermissionBasedGuard>
            </>
          }
        />

        <Grid
          container
          display="flex"
          alignItems="center"
          justifyContent="center" // Use justifyContent instead of justifyItems
        >
          <Grid item xs={12} md={12} spacing={2}>
            <Card
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center', // Use justifyContent instead of justifyItems
              }}
            >
              <Grid item xs={12} md={12}>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(3, 1fr)' },
                  }}
                >
                  <LocalizDatePicker
                    name="years"
                    label={translate('Report.FromDate')}
                    control={control}
                    size={'small'}
                    views={['year', 'month']}
                  />
                  <TextField
                    name="searchBy"
                    fullWidth
                    placeholder={translate('RentPayment.SearchBy')}
                    size="small"
                    value={searchBy} // Bind the state to the TextField
                    onChange={handleSearchChange} // Handle changes
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify
                            icon={'eva:search-fill'}
                            sx={{ color: 'text.disabled', width: 20, height: 20 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    sx={{ width: '120px' }}
                    variant="contained"
                    onClick={Search}
                    startIcon={<Iconify icon="eva:search-fill" />}
                  >
                    {/* {translate('Attendance.Filter')} */}
                    filter
                  </Button>
                </Box>
              </Grid>
              <Grid
                item
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} // Use justifyContent instead of justifyItems
              >
                <ElectricityConsumptionToolbar
                  Report={ElectricMeterConsumptionList}
                  year={Number(year)}
                  month={Number(month)}
                />
              </Grid>
            </Card>
          </Grid>
        </Grid>
        {isloading ? (
          <Loader height={100} />
        ) : Array.isArray(ElectricMeterConsumptionList) &&
          ElectricMeterConsumptionList.length > 0 ? (
          <>
            <Grid item xs={12} sx={{ width: '100%', mb: 2, mt: 2 }}>
              <Card
                sx={{
                  bgcolor: 'rgba(76, 175, 80, 0.2)',
                  p: 2,
                  width: '100%',
                  borderRadius: 2,
                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                }}
              >
                <TableContainer sx={{ paddingLeft: 2, paddingRight: 2 }}>
                  <Table sx={{ minWidth: '100%' }} size="small" aria-label="a dense table">
                    {/* <caption>A basic table example with a caption</caption> */}

                    <TableBody>
                      {/* First Row */}
                      <TableRow>
                        <TableCell align="left" sx={{ fontSize: '16px' }}>
                          {translate('Dashboards.TotalShop')} :
                        </TableCell>
                        <TableCell align="left" sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                          {totalShops}
                        </TableCell>

                        <TableCell align="left" sx={{ fontSize: '16px' }}>
                          {translate('ElectricMeter.Checked')} :
                        </TableCell>
                        <TableCell align="left" sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                          {totalPaid}
                        </TableCell>

                        <TableCell align="left" sx={{ fontSize: '16px' }}>
                          {translate('ElectricMeter.UnChecked')}:
                        </TableCell>
                        <TableCell align="left" sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                          {totalUnpaid}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>

            <Grid item xs={12} md={12} sx={{ mt: 2 }}>
              <Stack sx={{ p: 1 }}>
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 700 }} aria-label="customized table">
                    <TableHead>
                      <TableRow>
                        <TableCell>{translate('ElectricityPayment.No')}</TableCell>
                        <TableCell>{translate('ElectricMeter.ElectricMeterSerial')}</TableCell>
                        <TableCell>{translate('ElectricMeter.ElectricBoxCode')}</TableCell>
                        <TableCell>{translate('Shop.ShopCode')}</TableCell>
                        <TableCell>{translate('Customer.CustomerName')}</TableCell>
                        <TableCell>{translate('ElectricMeter.LastElectricMeterDegree')}</TableCell>
                        <TableCell>{translate('ElectricMeter.Consumption')}</TableCell>
                        <TableCell>{translate('GeneralFields.Action')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {SortByFieldSmart(ElectricMeterConsumptionList, "electricBox").map((row, index) => (
                        <TableRow key={index}>
                          <TableCell component="th" scope="row">
                            {index + 1}
                          </TableCell>
                          <TableCell>
                            {' '}
                            <Label color="warning"> {row.electricMeterSerial}</Label>
                          </TableCell>
                          <TableCell>
                            {' '}
                            {row.electricBox}
                          </TableCell>
                          <TableCell>
                            <Label color="success"> {row.shopCode}</Label>
                          </TableCell>
                          <TableCell>{row.customerName}</TableCell>
                          <TableCell>{row.lastElectricMeterDegree}</TableCell>
                          <TableCell align="left">
                            {row.hasPamentRecord ? (
                              <Label color="success">{translate('ElectricMeter.Checked')}</Label>
                            ) : (
                              <Label color="error">{translate('ElectricMeter.UnChecked')}</Label>
                            )}
                          </TableCell>

                          <TableCell>
                            {!row.hasPamentRecord ? (
                              <PermissionBasedGuard
                                permissions={['ElectricityPayment-RecieveElectricityPayment']}
                              >
                                <Button
                                  variant="contained"
                                  size="small"
                                  // style={{ display: row.remainAmount === 0 ? 'none' : '' }}
                                  // startIcon={<Iconify icon="eva:plus-fill" />}
                                  onClick={() => {
                                    if (row.electricMeterId) {
                                      handleConsumption(row.electricMeterId);
                                    }
                                  }}
                                >
                                  {translate('ElectricMeter.Consumption')}
                                </Button>
                              </PermissionBasedGuard>
                            ) : (
                              ''
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Grid>
          </>
        ) : (
          ''
        )}
        {(year || month) && (
          <MyDialog
            open={openConsuptionDialog}
            onClose={handleCloseConsumptionConfirm}
            title={translate('ElectricityPayment.Title')}
            size="md"
          >
            <ELectricConsumption
              id={ElectricMeterId}
              year={Number(year)}
              month={Number(month)}
              searchBy={searchBy}
            />
          </MyDialog>
        )}
      </Container>
    </Page>
  );
});
